// Derived views over the member list for the Team page panels.
//
// Pure functions over an ALREADY-FILTERED member array, so the panels and the
// list below them can never disagree: both read the same scoped set.
//
// ── Why these signals and not others ────────────────────────────────────────
// Measured read-only over the whole users collection on 2026-08-18 before
// picking what this page leads with (702 users / 69 orgs):
//
//   status = 'pending'      153/702  · real, varies per person      → used
//   no position             172/702  · real, varies per person      → used
//   no reporting manager    118/702  · real, varies per person      → used
//   stealth_mode = true     172/702  · real, varies per person      → used
//   billingEnabled = false    0/702  · degenerate — nobody, ever     → NOT used
//   isActivated                      · bimodal per ORG (35 orgs all-false,
//                                      33 all-true) — it's the org's payment
//                                      flag, not a person signal    → NOT used
//   department blank        466/702  · 60 of 69 orgs have none at all
//                                      → composition groups by POSITION, and
//                                        department stays a card sub-line only
//
// Leading with isActivated or billingEnabled would headline a number that is
// either always 0% or always 100% depending on the org — the fastest way to
// make a manager stop trusting the panel. The per-row activation dot in the
// table keeps its existing meaning; it just isn't promoted to a headline.

import { normalizeReportingManagerIds, roleLabel } from "./teamHelpers";

export const memberId = (u) => String(u?._id || u?.id || "");

const norm = (s) => String(s || "").trim();
const normLower = (s) => norm(s).toLowerCase();

export const hasManager = (u) => normalizeReportingManagerIds(u).length > 0;
export const hasPosition = (u) => norm(u?.position).length > 0;

// Ids that appear as somebody's reporting manager. Built from the FULL org list,
// never from the filtered set — otherwise filtering to "active only" turns a
// manager whose reports are all archived into a false orphan.
export function buildInsightContext(allUsers = []) {
  const managerIds = new Set();
  (allUsers || []).forEach((u) => {
    normalizeReportingManagerIds(u).forEach((id) => managerIds.add(String(id)));
  });
  return { managerIds };
}

export const EMPTY_CONTEXT = { managerIds: new Set() };

export const hasDirectReports = (u, ctx = EMPTY_CONTEXT) => ctx.managerIds.has(memberId(u));

// Someone with no manager but with direct reports is the ROOT of the org, not an
// orphan. Flagging the founder as a data problem is exactly the noise that makes
// a manager stop reading the panel.
export const isOrphan = (u, ctx = EMPTY_CONTEXT) => !hasManager(u) && !hasDirectReports(u, ctx);

// A member is "placed" once they sit somewhere in the chart — a position plus
// either a manager above them or reports below them.
export const isPlaced = (u, ctx = EMPTY_CONTEXT) =>
  hasPosition(u) && (hasManager(u) || hasDirectReports(u, ctx));

// ── Attention buckets ───────────────────────────────────────────────────────
// Each is a filter the members endpoint has no parameter for, so they run over
// the full org list client-side (see useTeamData) rather than over one page.
export const ATTENTION_BUCKETS = [
  {
    key: "pending",
    label: "Pending invite",
    tone: "warning",
    // "Pending" IS a status, and the status filter already owns that word. The
    // list defaults to Active, which hides exactly the people this row is for —
    // so this bucket flips the status control instead of filtering on top of it,
    // and its count is read from a status-agnostic set or it would always be 0.
    statusFilter: "pending",
    test: (u) => (u?.status || "active") === "pending",
  },
  {
    key: "noagent",
    label: "No desktop agent",
    tone: "negative",
    test: (u) => !u?.hasDesktopAgent,
  },
  {
    key: "nomanager",
    label: "No reporting manager",
    tone: "warning",
    test: isOrphan,
  },
  {
    key: "noposition",
    label: "No position set",
    tone: "neutral",
    test: (u) => !hasPosition(u),
  },
];

// Only the buckets the list filters client-side. A bucket backed by a real
// server filter (statusFilter) is driven through that control instead, so the
// two can never disagree about what the word means.
export const ATTENTION_KEYS = ATTENTION_BUCKETS.filter((b) => !b.statusFilter).map((b) => b.key);

export function attentionTest(key) {
  const bucket = ATTENTION_BUCKETS.find((b) => b.key === key);
  return bucket && !bucket.statusFilter ? bucket.test : null;
}

export function attentionLabel(key) {
  return ATTENTION_BUCKETS.find((b) => b.key === key)?.label || "";
}

// `statusAgnostic` is the same filtered set with the status filter left off. It
// is what status-backed buckets are counted against; the rest use `members`.
export function attentionCounts(members = [], ctx = EMPTY_CONTEXT, { statusAgnostic } = {}) {
  return ATTENTION_BUCKETS.map((b) => {
    const source = (b.statusFilter ? (statusAgnostic || members) : members) || [];
    return { ...b, count: source.filter((u) => b.test(u, ctx)).length };
  });
}

// ── Coverage ────────────────────────────────────────────────────────────────
export function coverageMetrics(members = [], ctx = EMPTY_CONTEXT) {
  const list = members || [];
  const total = list.length;
  const of = (key, label, test) => ({
    key, label, total,
    value: list.filter((u) => test(u, ctx)).length,
  });
  return [
    of("agent", "Desktop agent", (u) => !!u.hasDesktopAgent),
    of("stealth", "Background mode", (u) => !!u.stealth_mode),
    of("placed", "Placed in org chart", isPlaced),
  ];
}

// ── Composition ─────────────────────────────────────────────────────────────
// What the team is made of, grouped by whichever field this org actually fills
// in. Measured before choosing the order:
//
//   department — blank for 466/702 members; 60 of 69 orgs have none at all, so
//                a department ring would be one grey circle for nearly everyone.
//   position   — filled for 75%, but often near-unique: one org has 28 distinct
//                positions across 31 people. A top-N ring there is 71% "Other".
//   level      — 2 to 6 buckets by construction, and it's the shape a manager
//                actually thinks in. Available for 19 of 35 orgs.
//
// So: levels when the org has a structure, positions when it doesn't, role as
// the last resort. Whichever is used is named on the panel, so the ring never
// implies a grouping it isn't using.
export const NO_POSITION = "No position";
export const OTHER = "Other";

export function compositionBreakdown(members = [], { roles = [], structure = [], maxSlices = 8 } = {}) {
  const list = members || [];
  const total = list.length;
  if (!total) return { groupedBy: "position", total: 0, slices: [] };

  // Levels are only usable if members actually map onto them — a structure whose
  // positions nobody holds would render as one "Unassigned" ring.
  const levels = (structure || []).length ? orgLevels(structure, list) : [];
  const placedInLevels = levels.filter((l) => !l.isGap).reduce((n, l) => n + l.members.length, 0);
  if (placedInLevels > 0) {
    return {
      groupedBy: "level",
      total,
      slices: levels.map((l) => ({
        key: l.name, label: l.name, count: l.members.length,
        isGap: !!l.isGap, share: l.members.length / total,
      })),
    };
  }

  const positioned = list.filter(hasPosition);
  // No position anywhere in the org — group by role so the ring still says
  // something true instead of rendering a single "No position" circle.
  const groupedBy = positioned.length ? "position" : "role";

  const counts = new Map();
  list.forEach((u) => {
    const key = groupedBy === "position"
      ? (hasPosition(u) ? norm(u.position) : NO_POSITION)
      : (roleLabel(roles, u.role) || "Member");
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const unplaced = counts.get(NO_POSITION) || 0;
  counts.delete(NO_POSITION);

  const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  // Reserve a slot for "No position" so it is never what gets rolled into Other.
  const cap = Math.max(1, maxSlices - (unplaced ? 1 : 0));
  const head = ranked.slice(0, cap);
  const tail = ranked.slice(cap);
  const tailCount = tail.reduce((n, [, c]) => n + c, 0);

  const slices = head.map(([label, count]) => ({ key: label, label, count }));
  if (tailCount) slices.push({ key: OTHER, label: `${OTHER} (${tail.length})`, count: tailCount, isOther: true });
  if (unplaced) slices.push({ key: NO_POSITION, label: NO_POSITION, count: unplaced, isGap: true });

  return {
    groupedBy,
    total,
    slices: slices.map((s) => ({ ...s, share: s.count / total })),
  };
}

// ── Org levels ──────────────────────────────────────────────────────────────
// Levels come from the org's team structure; members map onto them by position.
// Roughly half of orgs have no structure configured, so callers must handle an
// empty result with a real empty state rather than an empty card.
export function orgLevels(structure = [], members = []) {
  const list = members || [];
  const levels = (structure || []).map((lvl, i) => ({
    name: norm(lvl?.levelName) || `Level ${i + 1}`,
    positions: (lvl?.positions || []).map((p) => normLower(p?.roleName)).filter(Boolean),
    members: [],
  }));

  const unassigned = [];
  list.forEach((u) => {
    const pos = normLower(u.position);
    const level = pos ? levels.find((l) => l.positions.includes(pos)) : null;
    if (level) level.members.push(u);
    else unassigned.push(u);
  });

  const out = levels.filter((l) => l.members.length > 0).map(({ name, members: m }) => ({ name, members: m }));
  if (unassigned.length) out.push({ name: "Unassigned", members: unassigned, isGap: true });
  return out;
}

// ── Reporting lines ─────────────────────────────────────────────────────────
// Who manages whom, and how many. Reports come from the scoped set (so the panel
// moves with the filters), but the manager themselves is resolved against the
// full org list — a manager can easily be filtered out of their own team's view.
export function managerSpans(members = [], allUsers = []) {
  const byId = new Map();
  (allUsers || []).forEach((u) => byId.set(memberId(u), u));
  (members || []).forEach((u) => { if (!byId.has(memberId(u))) byId.set(memberId(u), u); });

  const spans = new Map();
  (members || []).forEach((u) => {
    normalizeReportingManagerIds(u).forEach((id) => {
      const key = String(id);
      if (!spans.has(key)) spans.set(key, []);
      spans.get(key).push(u);
    });
  });

  return Array.from(spans.entries())
    .map(([id, reports]) => ({ id, manager: byId.get(id) || null, reports }))
    .filter((s) => s.manager)
    .sort((a, b) => b.reports.length - a.reports.length
      || String(a.manager.firstName || "").localeCompare(String(b.manager.firstName || "")));
}
